// Santé de SYNCHRO d'un magasin — seuils, âge d'un contact et ÉTAT, en UN endroit.
//
// ⚠️ UN SEUL CALCUL POUR TOUT CE QUI PARLE DE SYNCHRO : la page Supervision, la liste des magasins, la
// fiche magasin (sa ligne Synchronisation, ses tuiles Synchro et Caisses, son alerte) et la ligne « À traiter » lisent
// `storeHealth`. Deux copies d'un même jugement avaient déjà divergé — « en ligne » ici, « à jour » là-bas.
// ⚠️ L'ÉTAT D'UN MAGASIN EST CELUI DE SON APPAREIL LE PLUS MUET. Juger sur le contact le plus RÉCENT cachait une caisse
// morte derrière sa voisine en ligne : le magasin s'affichait « en ligne » pendant qu'une caisse encaissait seule depuis
// trois jours.
// ⚠️ UN SEUL VOCABULAIRE : en ligne · silencieux · hors ligne · jamais connecté (et « sans appareil » pour un dépôt qui
// n'en a aucun). « À jour », « en retard », « hors-ligne » sont abandonnés.
import { timeAgo as dureeEcoulee } from '@orgazone/date-format';
import { codeCaisse } from '@orgazone/ui-web';
import type { SupervisionStore } from './api';

/** Seuils de décrochage, en heures. Au-delà du second, un appareil est considéré durablement absent. */
export const SILENT_AFTER_H = 24;
export const OFFLINE_AFTER_H = 72;

/** Âge d'un contact, en heures — `null` si l'appareil n'a jamais parlé. */
export const ageHours = (iso: string | null): number | null =>
  (iso ? (Date.now() - new Date(iso).getTime()) / 3_600_000 : null);

/**
 * « il y a 2 h » — une durée se lit plus vite qu'une date quand la question est « est-ce récent ? ».
 * Le rendu vit dans `@orgazone/date-format` ; le nom reste exporté ici, c'est le vocabulaire de la supervision.
 */
export const timeAgo = (iso: string | null): string => dureeEcoulee(iso);

/** Une durée en clair : « 5 h », « 2 j 4 h ». */
export function fmtDuration(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 24) return `${Math.floor(hours)} h`;
  const d = Math.floor(hours / 24);
  const h = Math.floor(hours - d * 24);
  return h ? `${d} j ${h} h` : `${d} j`;
}

/** Les états d'un magasin — `na` : un dépôt sans appareil à superviser, qui n'entre dans aucun compteur. */
export type SyncKind = 'da' | 'wa' | 'ne' | 'ok' | 'na';
export const SYNC_LABEL: Record<SyncKind, string> = {
  da: 'hors ligne', wa: 'silencieux', ne: 'jamais connecté', ok: 'en ligne', na: 'sans appareil',
};

/** Un appareil qui a déjà parlé : son code (R01, MP01…), son dernier contact et sa version déclarée. */
export interface DeviceContact { code: string; till: boolean; at: number; hours: number; version: string | null }

export interface StoreHealth {
  kind: SyncKind;
  label: string;
  /** Pourquoi cet état, en clair (« R01 : aucun contact depuis 3 j ») — `null` quand tout va bien. */
  reason: string | null;
  /** L'appareil le plus muet parmi ceux qui ont déjà parlé — celui qu'on cite, partout. */
  worst: DeviceContact | null;
  tills: DeviceContact[];
  terminals: DeviceContact[];
  /** Caisses dont le dernier contact est sous le seuil de silence. */
  tillsOnline: number;
  /** Appareils affectés jamais mis en service : ils n'ont pas de contact à juger, mais ils comptent. */
  unpaired: number;
  /** Caisses dont la version déclarée n'est pas celle du canal du magasin — vide si la version attendue est inconnue. */
  late: DeviceContact[];
}

export type HealthInput = Pick<SupervisionStore, 'status' | 'error' | 'registers' | 'terminals'>
  & Partial<Pick<SupervisionStore, 'unpaired' | 'kind' | 'expectedVersion'>>;

/**
 * L'état de synchro d'un magasin.
 * ⚠️ « Jamais connecté » n'est PAS « hors ligne » : un appareil qui n'a jamais synchronisé vient d'être posé ou n'a jamais
 *    été appairé — le confondre avec une panne enverrait un technicien pour rien. Et un magasin pas encore mis en service
 *    n'a pas de base : l'erreur de lecture qui en résulte ne dit rien, elle n'est pas une panne.
 */
export function storeHealth(s: HealthInput, now = Date.now()): StoreHealth {
  const contact = (code: string, till: boolean, at: number, version: string | null): DeviceContact =>
    ({ code, till, at, hours: (now - at) / 3_600_000, version });
  // Un appareil n'est jugé que s'il a parlé : un contact à 0 est un curseur jamais écrit.
  const tills = s.registers.filter((r) => r.lastSeenAt > 0).map((r) => contact(codeCaisse(r.registerId), true, r.lastSeenAt, r.appVersion));
  const terminals = (s.terminals ?? []).filter((t) => t.lastSeenAt > 0)
    .map((t) => contact(t.equipmentCode ?? `terminal ${t.deviceId.slice(0, 8)}`, false, t.lastSeenAt, t.appVersion));
  const unpaired = s.unpaired ?? 0;
  const worst = [...tills, ...terminals].reduce<DeviceContact | null>((w, d) => (!w || d.hours > w.hours ? d : w), null);
  const tillsOnline = tills.filter((d) => d.hours <= SILENT_AFTER_H).length;
  const expected = s.expectedVersion ?? null;
  const late = expected ? tills.filter((d) => d.version !== null && d.version !== expected) : [];
  const base = { worst, tills, terminals, tillsOnline, unpaired, late };
  const of = (kind: SyncKind, reason: string | null): StoreHealth => ({ kind, label: SYNC_LABEL[kind], reason, ...base });

  if (s.status && s.status !== 'active') return of('ne', 'à mettre en service — pas encore de base');
  if (s.error) {
    // « does not exist » : la base n'a jamais été créée, aucun appareil n'a pu y écrire. Tout autre échec est une base
    // qui existait et ne répond plus — deux gestes opposés. ⚠️ La raison reste COURTE : le message réel de la base
    // s'affiche à côté (`s.error`), là où chaque écran a la place de le dire entier.
    return /does not exist/i.test(s.error) ? of('ne', 'base jamais synchronisée') : of('da', 'base injoignable');
  }
  if (!worst) {
    if (s.kind === 'depot' && unpaired === 0) return of('na', 'aucun appareil à superviser');
    return of('ne', unpaired ? 'aucun appareil n’a encore synchronisé' : 'aucun appareil appairé');
  }
  if (worst.hours > OFFLINE_AFTER_H) return of('da', `${worst.code} : aucun contact depuis ${fmtDuration(worst.hours)}`);
  if (worst.hours > SILENT_AFTER_H) return of('wa', `${worst.code} : aucun contact depuis ${fmtDuration(worst.hours)}`);
  return of('ok', null);
}

/** Le ton d'affichage d'un état — `na` se montre comme une attente, jamais comme une couleur. */
export const healthTone = (k: SyncKind): 'da' | 'wa' | 'ne' | 'ok' => (k === 'na' ? 'ne' : k);

/** L'ordre « du plus grave au plus sain » : on ouvre la supervision pour trouver ce qui ne va pas. */
export const HEALTH_ORDER: Record<SyncKind, number> = { da: 0, wa: 1, ne: 2, ok: 3, na: 4 };

/**
 * Les appareils muets depuis plus de 24 h, sur tout le parc, du plus muet au moins muet — la ligne « À traiter ».
 * ⚠️ DES APPAREILS, PAS DES MAGASINS : un magasin à trois caisses dont deux sont muettes en compte deux.
 */
export function mutedDevices(stores: readonly (HealthInput & { name: string })[], now = Date.now()): { store: string; device: DeviceContact }[] {
  const out: { store: string; device: DeviceContact }[] = [];
  for (const s of stores) {
    const h = storeHealth(s, now);
    if (s.status && s.status !== 'active') continue;
    for (const d of [...h.tills, ...h.terminals]) if (d.hours > SILENT_AFTER_H) out.push({ store: s.name, device: d });
  }
  return out.sort((a, b) => b.device.hours - a.device.hours);
}
